import React, { useState, useEffect } from 'react';
import { RefreshCw, Mail, FileText, Briefcase, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAllSubmissions = async () => {
    setLoading(true);
    try {
      const [contactsRes, quotesRes, applicationsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/contact/all`),
        fetch(`${BACKEND_URL}/api/quote/all`),
        fetch(`${BACKEND_URL}/api/careers/all`)
      ]);

      const contactsData = await contactsRes.json();
      const quotesData = await quotesRes.json();
      const applicationsData = await applicationsRes.json();

      setContacts(contactsData.contacts || []);
      setQuotes(quotesData.quotes || []);
      setApplications(applicationsData.applications || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllSubmissions();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-maritime-primary mb-2">
                Admin Dashboard
              </h1>
              <p className="text-gray-600">View all form submissions</p>
            </div>
            <Button
              onClick={fetchAllSubmissions}
              disabled={loading}
              className="bg-maritime-accent hover:bg-maritime-accent/90 text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <Tabs defaultValue="contacts" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="contacts" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Contact Forms ({contacts.length})
              </TabsTrigger>
              <TabsTrigger value="quotes" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Quote Requests ({quotes.length})
              </TabsTrigger>
              <TabsTrigger value="careers" className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Career Applications ({applications.length})
              </TabsTrigger>
            </TabsList>

            {/* Contact Forms */}
            <TabsContent value="contacts">
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <Card key={contact.id} className="border-2 border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-maritime-primary">
                            {contact.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {formatDate(contact.submitted_at)}
                          </p>
                        </div>
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">
                          Contact Form
                        </span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Email:</span>
                          <p className="text-gray-900">{contact.email}</p>
                        </div>
                        {contact.phone && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Phone:</span>
                            <p className="text-gray-900">{contact.phone}</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Message:</span>
                        <p className="text-gray-900 mt-1 whitespace-pre-wrap">{contact.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {contacts.length === 0 && !loading && (
                  <p className="text-center text-gray-500 py-8">No contact forms submitted yet</p>
                )}
              </div>
            </TabsContent>

            {/* Quote Requests */}
            <TabsContent value="quotes">
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <Card key={quote.id} className="border-2 border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-maritime-primary">
                            {quote.name}
                            {quote.company && (
                              <span className="text-gray-600 text-lg ml-2">
                                ({quote.company})
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {formatDate(quote.submitted_at)}
                          </p>
                        </div>
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                          Quote Request
                        </span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Email:</span>
                          <p className="text-gray-900">{quote.email}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Phone:</span>
                          <p className="text-gray-900">{quote.phone}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Urgency:</span>
                          <p className="text-gray-900 capitalize">{quote.urgency}</p>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Service:</span>
                          <p className="text-gray-900">{quote.service_type}</p>
                        </div>
                        {quote.vessel_type && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Vessel Type:</span>
                            <p className="text-gray-900">{quote.vessel_type}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-sm font-medium text-gray-600">Location:</span>
                          <p className="text-gray-900">{quote.location}</p>
                        </div>
                      </div>
                      {quote.details && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Details:</span>
                          <p className="text-gray-900 mt-1 whitespace-pre-wrap">{quote.details}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {quotes.length === 0 && !loading && (
                  <p className="text-center text-gray-500 py-8">No quote requests submitted yet</p>
                )}
              </div>
            </TabsContent>

            {/* Career Applications */}
            <TabsContent value="careers">
              <div className="space-y-4">
                {applications.map((app) => (
                  <Card key={app.id} className="border-2 border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-maritime-primary">
                            {app.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {formatDate(app.submitted_at)}
                          </p>
                        </div>
                        <span className="bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full">
                          Career Application
                        </span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Email:</span>
                          <p className="text-gray-900">{app.email}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Phone:</span>
                          <p className="text-gray-900">{app.phone}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Location:</span>
                          <p className="text-gray-900">{app.location}</p>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Position:</span>
                          <p className="text-gray-900 font-medium">{app.position}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Experience:</span>
                          <p className="text-gray-900">{app.experience}</p>
                        </div>
                      </div>
                      {app.resume && (
                        <div className="mb-4">
                          <span className="text-sm font-medium text-gray-600">Resume:</span>
                          <a
                            href={app.resume}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-maritime-accent hover:text-maritime-accent/80 flex items-center gap-1 mt-1"
                          >
                            View Resume
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      )}
                      <div>
                        <span className="text-sm font-medium text-gray-600">Cover Letter:</span>
                        <p className="text-gray-900 mt-1 whitespace-pre-wrap bg-gray-50 p-4 rounded">
                          {app.cover_letter}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {applications.length === 0 && !loading && (
                  <p className="text-center text-gray-500 py-8">No career applications submitted yet</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Admin;
